/**
 * View Component: RefreshButton
 * 紧凑版:36x36 圆形 icon 按钮(跟 Heart fav-count-pill 同高)
 *
 * ────────────────────────────────────────────────────────────────────────────
 * T-Cron-2026-10-31 03:30 早间 (T124) — 🔴 修「永久死按钮」
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 【本轮修的是什么】
 *
 * 线上实测(2026-10-31,canonical https://xhs-kanban.pages.dev/):
 *   <button class="refresh-btn refresh-btn-unavailable" disabled
 *           aria-label="刷新(未配置)" title="主动刷新需配置 VITE_REFRESH_URL 或在 PC 浏览器操作">
 *
 * ⇒ **每一个用户的 header 右上角都有一个 40% 透明度、永远点不动的灰色图标。**
 *
 * 根因(不是"环境没配",是设计选错了):
 *   REFRESH_URL = VITE_REFRESH_URL ?? (localhost ? 127.0.0.1:8765 : null)
 *   线上是 CF Pages 的 https 域名 ⇒ hostname 既不是 127.0.0.1 也不是 localhost
 *   ⇒ REFRESH_URL === null ⇒ 走 `if (!REFRESH_URL) return <disabled button/>` 分支。
 *   而 VITE_REFRESH_URL 在 CF Pages env 里**从未配置**(也没有 daemon 在跑,
 *   8765 端口无监听)⇒ **这个按钮在生产环境永远走不到它的正常逻辑**。
 *
 * 🔴 为什么这比"少一个功能"严重:
 *   · 用户看到的是一个**图标按钮**,视觉上与旁边可用的「安装」「导出」同款
 *     ⇒ 用户的合理预期是"点了会有反应";
 *   · 它是**唯一**一个"看得见但永远不可用"的控件,比"没有这个按钮"更伤
 *     —— 用户会以为看板坏了,或者以为自己漏看了什么;
 *   · 这正是 T123 在 toast 上抓到的同一类缺陷(**半盲功能**):
 *     键盘/静态测试全绿,但功能在生产里是坏的。
 *
 * 【本轮的做法:让它真的能用,而不是把它藏起来】
 *
 * 本 app **已经有**一套成熟的"新版本检测"机制(main.jsx 里):
 *   每 20s fetch('/sw.js?t=' + Date.now()) 比对 etag → 发现新版本 → toast → 自动 reload。
 * 这套机制**不依赖任何后端 daemon**,纯前端就能完成 ⇒
 * ⇒ 把刷新按钮接到这条既有通道上,语义就是「我现在就要最新数据」。
 *
 * 三种情形:
 *   ① 线上已有更新(sw.js 的 etag 变了)⇒ 提示「正在更新…」→ reload → 拿到新数据
 *   ② 线上已是最新 ⇒ 提示「已是最新」→ 2.5s 后自动回到 idle
 *   ③ 检查失败(离线 / 网络抖动)⇒ 提示「检查失败,点此重试」,按钮**保持可用**
 *      (不再把用户锁死在一个 disabled 的按钮上)
 *
 * 【不回归 daemon 通道】
 *   VITE_REFRESH_URL **如果**被配置了,仍然走原来的 daemon 路径
 *   (POST /refresh → 轮询 /status → 完成后 reload)——
 *   那是「立刻跑一次批次管线」的更强语义,不该被削弱。
 *
 * 【顺带修一个 React hooks 顺序 bug】
 *   原代码在 `if (!REFRESH_URL) return <disabled/>` **提前 return**,
 *   而 `React.useEffect(...)` 在它**下面** ⇒ 两个分支的 hook 数量不同。
 *   今天没崩,只是因为 REFRESH_URL 在整个构建里是常量(要么 null 要么有值),
 *   React 每次渲染看到的 hook 序列一致。
 *   但只要将来有人把 REFRESH_URL 改成**运行时**可变的(比如从 URL 参数读),
 *   同一组件树内两次渲染的 hook 数就会不一致 ⇒ React 直接抛
 *   「Rendered fewer hooks than expected」⇒ **整站白屏**。
 *   ⇒ 本轮把 useEffect **移到所有条件 return 之前**,从根上消除这个雷。
 *   (这与 T106 记录的「Hook 参数对象 TDZ」是同一类风险:静态全绿、执行期崩。)
 * ========================================================================== */
const ENV_URL = (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_REFRESH_URL) || null;
const LOCAL_URL = (typeof window !== "undefined" && (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost"))
  ? "http://127.0.0.1:8765"
  : null;
const REFRESH_URL = ENV_URL || LOCAL_URL;

// 🔴 T-Cron-2026-10-31 20:00 (T126) — 触感反馈(改进清单 #3)。
//   NOTE: 不要显式 `import React from 'react'` —— vite.config.js 的
//   esbuild.jsxInject 已经全局注入,再 import 会重复声明。
import { buzz } from '../harness/haptics.js';

function RefreshIcon({ spinning }) {
  // 🔴 T-Cron-2026-10-31 20:00 (T126) — 装饰性 svg 补 aria-hidden。
  //   这个 <svg> 是纯线条图标(无 <title>、无 role="img"),按钮的可访问名
  //   来自 aria-label="刷新" ⇒ svg 对读屏是**冗余**的。
  //   不加 aria-hidden 时,部分读屏会在「刷新」后面再追加一句
  //   「图像 / graphic」,并且可能把焦点落在图形节点上。
  //   ⇒ 与 PostCard / PostDetail / MobileFilterDrawer 的既有写法对齐。
  //   focusable="false" 是给 IE/Edge Legacy 的,加上无害。
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={spinning ? "refresh-icon-spin" : ""} aria-hidden="true" focusable="false">
      <polyline points="23 4 23 10 17 10"></polyline>
      <polyline points="1 20 1 14 7 14"></polyline>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
  );
}

/**
 * 读一次 sw.js 的版本戳(etag / last-modified),绕开 SW 缓存。
 * 返回 { ok, tag, changed }:
 *   ok=false     —— 网络失败(离线等)
 *   changed=true —— 线上版本与本地记录不同 ⇒ 有更新
 *
 * 口径说明:首次调用时**没有**基准可比,所以第一次只做「记录基准」,
 * 返回 changed=false。此时按钮会如实告诉用户「已是最新」——
 * 这在绝大多数情况下是对的(用户刚打开页面,内容就是最新的)。
 */
async function probeSwVersion(previousTag) {
  const res = await fetch("/sw.js?t=" + Date.now(), { cache: "no-store" });
  if (!res.ok) throw new Error("HTTP " + res.status);
  let tag = res.headers.get("etag") || res.headers.get("last-modified");
  if (!tag) {
    const text = await res.text();
    tag = text.slice(0, 200);
  }
  return { ok: true, tag: tag || "", changed: !!previousTag && !!tag && tag !== previousTag };
}

function RefreshButton() {
  // state: idle / checking / updating / done / failed
  const [state, setState] = React.useState("idle");
  const [jobId, setJobId] = React.useState(null);
  const [errMsg, setErrMsg] = React.useState("");
  // 原始异常只放进 title(悬停可见),aria-label 用人话 —— 读屏用户
  // 不需要听 "TypeError: Failed to fetch"。
  const [errDetail, setErrDetail] = React.useState("");
  const [countdown, setCountdown] = React.useState(0);
  const pollRef = React.useRef(null);
  const timerRef = React.useRef(null);
  // 记住上一次见到的 sw 版本戳,作为「有没有更新」的比较基准。
  const baseTagRef = React.useRef(null);

  // ⚠️ hooks 顺序:所有 useEffect 必须在任何条件 return 之前。
  //    (见文件头「顺带修一个 React hooks 顺序 bug」)
  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // 空闲若干秒后自动从 done/failed 回到 idle,避免按钮长期挂着状态色。
  React.useEffect(() => {
    if (state !== "done" && state !== "failed") return undefined;
    const t = setTimeout(() => {
      setState("idle");
      setErrMsg("");
      setErrDetail("");
      setCountdown(0);
    }, 2600);
    return () => clearTimeout(t);
  }, [state]);

  /** 「我就要最新数据」—— 无 daemon 时的通道:查 sw 版本,有更新就 reload。 */
  const checkAndReload = React.useCallback(async () => {
    if (state === "checking" || state === "updating") return;
    setState("checking");
    setErrMsg("");
    setErrDetail("");
    try {
      const probe = await probeSwVersion(baseTagRef.current);
      // 第一次调用只建立基准;之后每次都能真正比对出「有没有更新」。
      if (baseTagRef.current === null) baseTagRef.current = probe.tag;
      if (probe.changed) {
        setState("updating");
        // 让浏览器先把新 sw.js 拉下来(交给 SW 自己升级),再 reload。
        if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
          try {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => (r.update ? r.update() : Promise.resolve())));
          } catch (_) { /* 拿不到就算了,reload 依然会拉新的 */ }
        }
        window.location.reload();
        return;
      }
      setState("done");
    } catch (e) {
      // 离线 / 网络失败 —— **不能把按钮锁死**,必须让用户能重试。
      setState("failed");
      setErrMsg("检查失败，请检查网络后重试");
    }
  }, [state]);

  /** daemon 通道:VITE_REFRESH_URL 配置时,跑一次 evening 批次管线。 */
  const runDaemonRefresh = React.useCallback(async () => {
    setState("checking");
    setErrMsg("");
    setErrDetail("");
    setJobId(null);
    try {
      const res = await fetch(REFRESH_URL + "/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batch: "evening" }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 120)}`);
      }
      const data = await res.json();
      setJobId(data.job_id);
      setState("updating");
      pollRef.current = setInterval(async () => {
        try {
          const r = await fetch(`${REFRESH_URL}/status?job=${data.job_id}`);
          if (!r.ok) return;
          const s = await r.json();
          if (s.state === "done") {
            setState("done");
            clearInterval(pollRef.current);
            setCountdown(3);
            timerRef.current = setInterval(() => {
              setCountdown((c) => {
                if (c <= 1) {
                  clearInterval(timerRef.current);
                  window.location.reload();
                  return 0;
                }
                return c - 1;
              });
            }, 1000);
          } else if (s.state === "failed") {
            setState("failed");
            setErrMsg(`rc=${s.rc},daemon log: scripts/logs/daemon.log`);
            clearInterval(pollRef.current);
          }
        } catch (e) {
          // 网络抖动,继续轮询
        }
      }, 1000);
    } catch (e) {
      setState("failed");
      // 🔴 不要把原始异常抛给用户看。
      //    `String(e)` 会得到 "TypeError: Failed to fetch" 这种
      //    **开发者文案** —— 它对用户零信息量,却让人以为看板坏了。
      //    ⇒ 用户看到人话,细节留在 title 里(悬停可见)。
      setErrMsg("刷新服务未响应，请稍后重试");
      setErrDetail(String(e).slice(0, 200));
    }
  }, []);

  const handleClick = React.useCallback(() => {
    // 🔴 触感反馈(改进清单 #3)。
    //   刷新是一个「我按了,它要去做点什么」的动作(有网络往返),
    //   但按钮视觉反馈要等到 checking 状态才出现(有一个网络延迟的窗口)。
    //   ⇒ 按下的瞬间给一次轻触感,补上这段「静默窗口」。
    //   口径与全站一致:harness/haptics.js 内部已处理设备不支持 /
    //   prefers-reduced-motion / 异常吞掉三种情况。
    buzz(8);
    if (REFRESH_URL) runDaemonRefresh();
    else checkAndReload();
  }, [runDaemonRefresh, checkAndReload]);

  const titleMap = {
    idle: REFRESH_URL
      ? `调 daemon POST /refresh (${REFRESH_URL})`
      : "检查是否有新内容,有就自动刷新页面",
    checking: "正在检查…",
    updating: countdown > 0 ? `已刷新 (${countdown}s 后自动刷新)` : "正在更新…",
    done: countdown > 0 ? `已刷新 (${countdown}s 后自动刷新)` : "已是最新",
    failed: errMsg || "失败",
  };

  // title 带原始异常细节(给要排查的人),aria-label 只给人话(给读屏)。
  const titleText = state === "failed" && errDetail
    ? `${errMsg || "失败"} — ${errDetail}`
    : titleMap[state];

  const cls = `refresh-btn refresh-btn-${state}`;
  const spinning = state === "checking" || state === "updating";

  // 🔴 T-Cron-2026-10-31 14:00 (T125) — 修「aria-live 挂在没有文本的按钮上」
  // ──────────────────────────────────────────────────────────────────────
  // 上一轮(T124)把刷新按钮从「永久 disabled」改成了真正可用的三态流程
  // (checking → done / failed),但**状态反馈只对视觉用户有效**:
  //
  //   <button aria-label={titleMap[state]} aria-live="polite">
  //     <RefreshIcon />        ← 里面只有一个 <svg>,没有任何文本节点
  //   </button>
  //
  // 【为什么 aria-live 在这里完全不起作用】
  //   aria-live 的语义是「**这个区域里的文本内容发生变化时,播报新内容**」。
  //   而这个按钮的可访问名来自 aria-label(一个**属性**),不是文本内容。
  //   live region 监控的是 DOM 文本节点的变更 —— 按钮里压根没有文本节点。
  //   ⇒ 状态从 idle → checking → done 变化时,live region 观测到的
  //     文本变更量是 **0** ⇒ 读屏软件**一声不吭**。
  //   ⇒ 更糟的是 aria-label 变了:读屏软件在用户**重新聚焦**这个按钮时
  //     会念出新 label,但「按下去 → 等待 → 听到结果」这条主路径是哑的。
  //   ⇒ 对读屏用户来说,这个按钮依然是「按了没反应」——
  //     恰好是 T124 想消灭的那种「半盲功能」,只是换了个形态。
  //
  // 【本轮的做法】
  //   把 live region 挪到按钮**外面**,放在一个真正含文本的 .sr-only 元素上
  //   —— 这正是本仓库既有的标准做法(PostDetail.jsx 的复制结果播报、
  //   ContentGrid.jsx 的虚拟滚动播报,都是这个结构)。
  //
  //   · 按钮本身去掉 aria-live(留着只会误导后来的人以为已经有播报);
  //   · 新增 <span className="sr-only" role="status" aria-live="polite">
  //       {state === 'idle' ? '' : titleMap[state]}
  //     </span>
  //   · idle 时内容为空串 ⇒ live region 里没有变化 ⇒ 不会在页面加载时
  //     莫名其妙播报一句「检查是否有新内容」;
  //   · aria-atomic="true" ⇒ 整句替换,读屏念完整的新状态而不是拼接片段。
  //
  // 【为什么 aria-label 保留】
  //   aria-label 负责「这个按钮是干什么的」(读屏用户 Tab 到它时的名字),
  //   live region 负责「它刚才干了什么」(按下之后的结果播报)。
  //   两者职责不同,都要留着。
  const liveMsg = state === "idle" ? "" : titleMap[state];

  return (
    <>
      <button
        className={cls}
        onClick={handleClick}
        disabled={spinning}
        title={titleText}
        aria-label={titleMap[state]}
      >
        <RefreshIcon spinning={spinning} />
      </button>
      {/* 读屏专用播报区:按钮的状态变化在这里播报。
          注意它必须**在按钮外面**且**必须有文本内容** ——
          aria-live 挂在只含 svg 的按钮上是哑的(见上方说明)。 */}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveMsg}
      </span>
    </>
  );
}

window.RefreshButton = RefreshButton;

export default RefreshButton;
